import { useSseStream } from '@/shared/hooks/useSseStream';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { EventSourceMessage } from '@microsoft/fetch-event-source';
import { useCallback, useEffect, useRef } from 'react';
import { catchUpMessaging, direcionamentoFailed, direcionamentoPendenciaCreated, direcionamentoResolved, negociacaoCasoStatusUpdated } from '../store/actions';
import { selectLastEventAt, selectLastEventId } from '../store/selectors';
import { addMensagem, setLastEventAt, setLastEventId, updateConversa, updateMensagemStatus } from '../store/slice';
import { type Conversa, type DirectionPendenciaEvent, type DirectionResolutionEvent, type Mensagem, type MessagingEvent, type NegociacaoCasoEvent } from '../types/messaging.types';
const POLLING_INTERVAL_MS = 5000;
export const useMessagingRealtime = (empresaId: string, escritorioId?: string) => {
  const dispatch = useAppDispatch();
  const lastEventAt = useAppSelector(selectLastEventAt);
  const lastEventId = useAppSelector(selectLastEventId);
  const lastEventAtRef = useRef(lastEventAt);
  const lastEventIdRef = useRef(lastEventId);
  useEffect(() => {
    lastEventAtRef.current = lastEventAt;
    lastEventIdRef.current = lastEventId;
  }, [lastEventAt, lastEventId]);
  const handleStreamMessage = useCallback((message: EventSourceMessage) => {
    if (message.id) {
      dispatch(setLastEventId(message.id));
    }
    try {
      const parsedEvent: MessagingEvent = JSON.parse(message.data);
      const {
        data,
        occurredAt
      } = parsedEvent;
      dispatch(setLastEventAt(occurredAt));
      if ('kind' in data && (data as DirectionPendenciaEvent).kind === 'direction_pendencia_created') {
        dispatch(direcionamentoPendenciaCreated((data as DirectionPendenciaEvent).pendencia));
      } else if ('kind' in data && ((data as DirectionResolutionEvent).kind === 'direction_resolved' || (data as DirectionResolutionEvent).kind === 'direction_failed')) {
        const resolution = data as DirectionResolutionEvent;
        const payload = {
          negociacaoCasoId: resolution.negociacaoCasoId,
          pendenciaId: resolution.pendenciaId
        };
        dispatch(resolution.kind === 'direction_resolved' ? direcionamentoResolved(payload) : direcionamentoFailed(payload));
      } else if ('entityType' in data && (data as NegociacaoCasoEvent).entityType === 'negociacaoCaso') {
        const casoEvent = data as NegociacaoCasoEvent;
        dispatch(negociacaoCasoStatusUpdated({
          caseId: casoEvent.id,
          status: casoEvent.status
        }));
      } else if ('conversaId' in data) {
        const mensagem = data as Mensagem;
        dispatch(addMensagem(mensagem));
        if (mensagem.status !== 'PENDING') {
          dispatch(updateMensagemStatus({
            id: mensagem.id,
            conversaId: mensagem.conversaId,
            status: mensagem.status
          }));
        }
      } else {
        dispatch(updateConversa(data as Conversa));
      }
    } catch {
      // ignora evento malformado — manter o stream vivo
    }
  }, [dispatch]);
  const handleStreamOpen = useCallback(() => {
    if (lastEventAtRef.current) {
      dispatch(catchUpMessaging());
    }
  }, [dispatch]);
  const {
    isConnected
  } = useSseStream({
    path: '/conversas/realtime/stream',
    enabled: Boolean(empresaId),
    escritorioId,
    onMessage: handleStreamMessage,
    onOpen: handleStreamOpen
  });

  // Polling de fallback: ativo enquanto SSE estiver desconectado.
  // Usa catchUpMessaging (busca só o que mudou desde lastEventAt).
  useEffect(() => {
    if (isConnected || !empresaId) return;
    const intervalId = setInterval(() => {
      if (lastEventAtRef.current) {
        dispatch(catchUpMessaging());
      }
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isConnected, empresaId, dispatch]);
  return {
    isConnected
  };
};